/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetMcpServerAuthenticationRequest,
  GetMcpServerAuthenticationSecurity,
} from "../models/operations/getmcpserverauthentication.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildMcpServerAuthenticationQuery,
  McpServerAuthenticationQueryData,
  prefetchMcpServerAuthentication,
  queryKeyMcpServerAuthentication,
} from "./mcpServerAuthentication.core.js";
export {
  buildMcpServerAuthenticationQuery,
  type McpServerAuthenticationQueryData,
  prefetchMcpServerAuthentication,
  queryKeyMcpServerAuthentication,
};

export type McpServerAuthenticationQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getMcpServerAuthentication authClients
 *
 * @remarks
 * Get how an MCP server authenticates to its upstream: whether authentication is required and which auth clients it offers. Requires org:read.
 */
export function useMcpServerAuthentication(
  request: GetMcpServerAuthenticationRequest,
  security?: GetMcpServerAuthenticationSecurity | undefined,
  options?: QueryHookOptions<
    McpServerAuthenticationQueryData,
    McpServerAuthenticationQueryError
  >,
): UseQueryResult<
  McpServerAuthenticationQueryData,
  McpServerAuthenticationQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildMcpServerAuthenticationQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getMcpServerAuthentication authClients
 *
 * @remarks
 * Get how an MCP server authenticates to its upstream: whether authentication is required and which auth clients it offers. Requires org:read.
 */
export function useMcpServerAuthenticationSuspense(
  request: GetMcpServerAuthenticationRequest,
  security?: GetMcpServerAuthenticationSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    McpServerAuthenticationQueryData,
    McpServerAuthenticationQueryError
  >,
): UseSuspenseQueryResult<
  McpServerAuthenticationQueryData,
  McpServerAuthenticationQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildMcpServerAuthenticationQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setMcpServerAuthenticationData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: McpServerAuthenticationQueryData,
): McpServerAuthenticationQueryData | undefined {
  const key = queryKeyMcpServerAuthentication(...queryKeyBase);

  return client.setQueryData<McpServerAuthenticationQueryData>(key, data);
}

export function invalidateMcpServerAuthentication(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      mcpServerId: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "authClients",
      "getMcpServerAuthentication",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllMcpServerAuthentication(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "getMcpServerAuthentication"],
  });
}
